import React from "react";
import {autobind} from "core-decorators";
import {Button, message, Popconfirm, Tooltip} from "antd";
import {FaBook} from "react-icons/fa";
import {runInAction} from "mobx";
import {inject, observer} from "mobx-react";

import {CorrectPieceStatuses, PieceStatuses} from "@/shared/constants";
import {PiecesQuery} from "@/graphql/types";
import pieceStatusUpdate from "@/graphql/queries/pieceStatusUpdate";
import Store from "@/stores";

export interface IPublishActionProps {
  piece: PiecesQuery["pieces"][0];
  store?: Store;
}

@inject("store")
@autobind
@observer
export default class PublishAction extends React.Component<IPublishActionProps> {
  async publishPiece() {
    const {piece} = this.props;
    const initialStatus = piece.status;
    runInAction(() => (piece.status = PieceStatuses.Published)); // optimistic UI update
    try {
      await pieceStatusUpdate(this.props.store!.apollo.client, piece.id, PieceStatuses.Published);
    } catch (error) {
      console.error(error);
      runInAction(() => (piece.status = initialStatus)); // undo optimistic UI update
      message.error("There was an error while publishing the piece");
    }
  }

  async unpublishPiece() {
    const {piece} = this.props;
    const initialStatus = piece.status;
    runInAction(() => (piece.status = PieceStatuses.Draft)); // optimistic UI update
    try {
      await pieceStatusUpdate(this.props.store!.apollo.client, piece.id, PieceStatuses.Draft);
    } catch (error) {
      console.error(error);
      runInAction(() => (piece.status = initialStatus)); // undo optimistic UI update
      message.error("There was an error while unpublishing the piece");
    }
  }

  public render() {
    const {piece, store} = this.props;
    if (!store!.session.isEnsembleConductor) return null;
    if (!CorrectPieceStatuses.find((status) => status === piece.status))
      return (
        <Tooltip title="Publish" placement="topLeft" arrowPointAtCenter>
          <Button type="link" shape="circle" size="small" disabled>
            <FaBook />
          </Button>
        </Tooltip>
      );
    else if (piece.status === PieceStatuses.Draft)
      return (
        <Tooltip title="Publish" placement="topLeft" arrowPointAtCenter>
          <Popconfirm
            title="Do you want to publish this piece? It will be visible for all ensemble members."
            placement="topRight"
            arrowPointAtCenter
            onConfirm={this.publishPiece}
          >
            <Button type="link" shape="circle" size="small">
              <FaBook />
            </Button>
          </Popconfirm>
        </Tooltip>
      );
    else if (piece.status === PieceStatuses.Published)
      return (
        <Tooltip title="Unpublish" placement="topLeft" arrowPointAtCenter>
          <Popconfirm
            title="Do you want to unpublish this piece? It will return to draft status."
            placement="topRight"
            arrowPointAtCenter
            onConfirm={this.unpublishPiece}
          >
            <Button type="link" shape="circle" size="small">
              <FaBook />
            </Button>
          </Popconfirm>
        </Tooltip>
      );
  }
}
